import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // Q10 勾「其他，請說明」→ 顯示 Q11 文字題補充說明
  // 該選項 o_type 0（非半開放，說明改由 Q11 收），故用位置 10，不可寫 "other"
  // （第 11 項「順其自然」是互斥，依規範放最後）
  const visibleIfConditionsInit = convertVisibleIf({
    q11: '{q10} anyof [10]',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  // 篩選條件 → 任一條件成立直接結束問卷
  // q1=以上時間皆不方便 / q5=29歲以下或70歲以上 / q7=非自己有吃且自己購買 /
  // q9=完全沒有 / q10=目前無上述困擾，對延緩衰老抱持順其自然的態度 / q13 未選購買保健食品
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression:
        `({q1} anyof ["none"]) or ` +
        `({q5} = 1 or {q5} = 6) or ` +
        `({q7} = 2 or {q7} = 3 or {q7} = 4) or ` +
        `({q9} = 1) or ` +
        `({q10} anyof ["none"]) or ` +
        // 必須加 notempty：notcontains 對空值也成立，拆頁時前面頁按下一頁就會誤觸結束
        `({q13} notempty and {q13} notcontains [1])`,
    },
  ])
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 資格篩出文案（與 triggersConditionsInit 1:1 對應）
  const endMessage = `目前與您相同特性的樣本已額滿，沒辦法參與本次活動。<br>感謝您撥冗填答，祝您有美好的一天！`

  // 正常完成結語（客製，含典通客服資訊）
  const completedMessage = `<div>再次感謝您的回答。<br>若您符合參加座談會的資格，我們會轉由委託的市場研究公司「典通股份有限公司」與您聯繫，還請留意接聽來電（2331開頭），以維護您活動參與的權益。<br>祝您有個美好的一天。<br><br>若有調查相關問題，請洽典通市場調查公司<br>典通客服專線<br>02-23315133#819 陳小姐<br>02-23315133#839 蘇小姐<br>問卷主題：日常生活保健座談會</div>`

  const endSurveyMsg = (survey) => {
    if (
      survey.getValue(q(1))?.[0] === 'none' ||
      survey.getValue(q(5)) === v(1) ||
      survey.getValue(q(5)) === v(6) ||
      survey.getValue(q(7)) === v(2) ||
      survey.getValue(q(7)) === v(3) ||
      survey.getValue(q(7)) === v(4) ||
      survey.getValue(q(9)) === v(1) ||
      survey.getValue(q(10))?.[0] === 'none' ||
      (survey.getValue(q(13))?.length > 0 &&
        !survey.getValue(q(13)).includes(v(1)))
    ) {
      setCompletionMessage(endMessage)
    } else {
      setCompletionMessage(completedMessage)
    }
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
